// ============================================
// Digital Signs - Display Setup Page
// Shows on display with pairing instructions
// Collects device fingerprint automatically
// URL: /display or /setup
// ============================================

import React, { useState, useEffect, useCallback, useRef } from 'react';

const API_BASE = '/api/trpc';

// Generate device fingerprint
async function collectDeviceFingerprint() {
  const fp = {
    // Screen info
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    screenAvailWidth: window.screen.availWidth,
    screenAvailHeight: window.screen.availHeight,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    colorDepth: window.screen.colorDepth,
    pixelRatio: window.devicePixelRatio || 1,
    
    // Browser/OS info
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    languages: navigator.languages ? navigator.languages.join(',') : navigator.language,
    cookiesEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack,
    
    // Hardware
    cpuCores: navigator.hardwareConcurrency || null,
    deviceMemory: navigator.deviceMemory || null,
    maxTouchPoints: navigator.maxTouchPoints || 0,
    
    // Timezone
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: new Date().getTimezoneOffset(),
    
    // Date collected
    collectedAt: new Date().toISOString(),
  };

  // Parse user agent for OS/Browser
  const ua = navigator.userAgent;
  fp.browserName = getBrowserName(ua);
  fp.browserVersion = getBrowserVersion(ua);
  fp.osName = getOSName(ua);
  fp.osVersion = getOSVersion(ua);

  // Canvas fingerprint
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 200;
    canvas.height = 50;
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('Digital Signs', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Fingerprint', 4, 17);
    fp.canvasFingerprint = canvas.toDataURL().slice(-50);
  } catch (e) {
    fp.canvasFingerprint = null;
  }

  // WebGL info
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        fp.webglVendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
        fp.webglRenderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      }
      fp.webglVersion = gl.getParameter(gl.VERSION);
    }
  } catch (e) {
    fp.webglVendor = null;
    fp.webglRenderer = null;
  }

  // Audio fingerprint (basic)
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    fp.audioSampleRate = audioContext.sampleRate;
    audioContext.close();
  } catch (e) {
    fp.audioSampleRate = null;
  }

  // Generate hash fingerprint
  const fpString = JSON.stringify({
    screen: fp.screenResolution,
    colorDepth: fp.colorDepth,
    pixelRatio: fp.pixelRatio,
    timezone: fp.timezone,
    language: fp.language,
    platform: fp.platform,
    cpuCores: fp.cpuCores,
    webglRenderer: fp.webglRenderer,
    canvas: fp.canvasFingerprint,
  });
  fp.fingerprintHash = await hashString(fpString);

  return fp;
}

// Simple hash function
async function hashString(str) {
  const msgBuffer = new TextEncoder().encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Parse browser name from user agent
function getBrowserName(ua) {
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('SamsungBrowser')) return 'Samsung Browser';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  if (ua.includes('Trident')) return 'Internet Explorer';
  if (ua.includes('Edge')) return 'Edge Legacy';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  return 'Unknown';
}

// Parse browser version
function getBrowserVersion(ua) {
  let match;
  if ((match = ua.match(/Firefox\/(\d+)/))) return match[1];
  if ((match = ua.match(/SamsungBrowser\/(\d+)/))) return match[1];
  if ((match = ua.match(/OPR\/(\d+)/))) return match[1];
  if ((match = ua.match(/Edg\/(\d+)/))) return match[1];
  if ((match = ua.match(/Chrome\/(\d+)/))) return match[1];
  if ((match = ua.match(/Safari\/(\d+)/))) return match[1];
  return 'Unknown';
}

// Parse OS name
function getOSName(ua) {
  if (ua.includes('Windows NT 10')) return 'Windows 10/11';
  if (ua.includes('Windows NT 6.3')) return 'Windows 8.1';
  if (ua.includes('Windows NT 6.2')) return 'Windows 8';
  if (ua.includes('Windows NT 6.1')) return 'Windows 7';
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac OS X')) return 'macOS';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  if (ua.includes('CrOS')) return 'Chrome OS';
  return 'Unknown';
}

// Parse OS version
function getOSVersion(ua) {
  let match;
  if ((match = ua.match(/Windows NT (\d+\.\d+)/))) return match[1];
  if ((match = ua.match(/Mac OS X (\d+[._]\d+)/))) return match[1].replace('_', '.');
  if ((match = ua.match(/Android (\d+(\.\d+)?)/))) return match[1];
  if ((match = ua.match(/iPhone OS (\d+_\d+)/))) return match[1].replace('_', '.');
  return 'Unknown';
}

// API helper
async function apiCall(procedure, input = {}) {
  const res = await fetch(`${API_BASE}/${procedure}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error.message || 'API Error');
  return data.result?.data;
}

// Icons
const Monitor = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="hero-icon">
    <rect width="20" height="14" x="2" y="3" rx="2"/>
    <line x1="8" x2="16" y1="21" y2="21"/>
    <line x1="12" x2="12" y1="17" y2="21"/>
  </svg>
);

const CheckCircle = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="status-icon success">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const Clock = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="status-icon waiting">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const Wifi = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="info-icon">
    <path d="M5 12.55a11 11 0 0 1 14.08 0"/>
    <path d="M1.42 9a16 16 0 0 1 21.16 0"/>
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
    <line x1="12" x2="12.01" y1="20" y2="20"/>
  </svg>
);

const Loader = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spin" style={{ width: 24, height: 24 }}>
    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
  </svg>
);

// Styles
const styles = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@500;700&display=swap');

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  background: linear-gradient(135deg, #0a0f1a 0%, #1a1f3a 50%, #0a0f1a 100%);
  color: white;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.02); }
}

@keyframes glow {
  0%, 100% { box-shadow: 0 0 30px rgba(59, 130, 246, 0.3); }
  50% { box-shadow: 0 0 60px rgba(59, 130, 246, 0.5); }
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

@keyframes gradient {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.setup-container {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  position: relative;
  overflow: hidden;
}

.background-grid {
  position: absolute;
  inset: 0;
  background-image: 
    linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px);
  background-size: 50px 50px;
  pointer-events: none;
}

.hero-section {
  text-align: center;
  margin-bottom: 48px;
  animation: float 6s ease-in-out infinite;
}

.hero-icon {
  width: 120px;
  height: 120px;
  color: #3b82f6;
  margin-bottom: 24px;
  filter: drop-shadow(0 0 30px rgba(59, 130, 246, 0.4));
}

.hero-title {
  font-size: 48px;
  font-weight: 800;
  background: linear-gradient(135deg, #60a5fa, #3b82f6, #818cf8);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: gradient 4s ease infinite;
  margin-bottom: 12px;
}

.hero-subtitle {
  font-size: 20px;
  color: #94a3b8;
  font-weight: 400;
}

.pairing-card {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 32px;
  padding: 48px 64px;
  text-align: center;
  max-width: 600px;
  width: 100%;
  animation: glow 3s ease-in-out infinite;
}

.step-label {
  font-size: 14px;
  font-weight: 600;
  color: #3b82f6;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 16px;
}

.instruction-text {
  font-size: 24px;
  font-weight: 500;
  color: #e2e8f0;
  margin-bottom: 24px;
  line-height: 1.4;
}

.url-display {
  background: rgba(59, 130, 246, 0.1);
  border: 2px solid rgba(59, 130, 246, 0.3);
  border-radius: 16px;
  padding: 20px 32px;
  margin-bottom: 40px;
}

.url-text {
  font-family: 'JetBrains Mono', monospace;
  font-size: 28px;
  font-weight: 700;
  color: #60a5fa;
  word-break: break-all;
}

.divider {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 40px;
  color: #475569;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
}

.pin-section {
  margin-bottom: 32px;
}

.pin-label {
  font-size: 16px;
  color: #94a3b8;
  margin-bottom: 16px;
}

.pin-display {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.pin-digit {
  width: 72px;
  height: 96px;
  background: linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05));
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'JetBrains Mono', monospace;
  font-size: 48px;
  font-weight: 700;
  color: white;
  text-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
}

.status-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px 24px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  margin-bottom: 24px;
}

.status-icon {
  width: 24px;
  height: 24px;
}

.status-icon.waiting {
  color: #f59e0b;
  animation: pulse 2s ease-in-out infinite;
}

.status-icon.success {
  color: #22c55e;
}

.status-text {
  font-size: 16px;
  color: #94a3b8;
}

.device-info {
  margin-top: 32px;
  padding-top: 32px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.device-info-title {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 16px;
}

.device-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  text-align: left;
}

.info-item {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  padding: 12px;
}

.info-label {
  font-size: 11px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.info-value {
  font-size: 14px;
  color: #e2e8f0;
  font-weight: 500;
}

.footer {
  position: absolute;
  bottom: 24px;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 12px;
  color: #475569;
}

/* Success state */
.success-container {
  text-align: center;
}

.success-icon-large {
  width: 100px;
  height: 100px;
  color: #22c55e;
  margin-bottom: 24px;
  animation: pulse 2s ease-in-out infinite;
}

.success-title {
  font-size: 36px;
  font-weight: 700;
  color: #22c55e;
  margin-bottom: 12px;
}

.success-message {
  font-size: 18px;
  color: #94a3b8;
  margin-bottom: 24px;
}

.redirect-notice {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #64748b;
  font-size: 14px;
}

/* Loading state */
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10, 15, 26, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 3px solid rgba(59, 130, 246, 0.2);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.loading-text {
  color: #94a3b8;
  font-size: 16px;
}
`;

export default function SetupPage() {
  const [status, setStatus] = useState('initializing'); // initializing, collecting, waiting, approved, error
  const [pinCode, setPinCode] = useState('------');
  const [displayId, setDisplayId] = useState(null);
  const [displayToken, setDisplayToken] = useState(null);
  const [fingerprint, setFingerprint] = useState(null);
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  // Get the pairing URL
  const pairingUrl = window.location.origin + '/pair';

  // Initialize - register as pending display or use existing
  useEffect(() => {
    initializeDisplay();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const initializeDisplay = async () => {
    try {
      setStatus('collecting');
      
      // Collect device fingerprint
      const fp = await collectDeviceFingerprint();
      setFingerprint(fp);

      // Check if we have existing credentials
      const storedToken = localStorage.getItem('displayToken');
      const storedId = localStorage.getItem('displayId');

      if (storedToken && storedId) {
        // Verify existing display and update fingerprint
        try {
          const result = await apiCall('displays.verifyAndUpdate', {
            displayId: parseInt(storedId),
            token: storedToken,
            deviceInfo: fp,
            fingerprint: fp.fingerprintHash,
          });

          if (result.isPaired) {
            setDisplayId(storedId);
            setDisplayToken(storedToken);
            setPinCode(result.pinCode || '------');
            setStatus('approved');
            
            // Redirect to player
            setTimeout(() => {
              window.location.href = '/player';
            }, 2000);
            return;
          }
          
          // Display exists but needs re-approval (fingerprint changed)
          if (result.pendingApproval) {
            setDisplayId(storedId);
            setDisplayToken(storedToken);
            setPinCode(result.pinCode || '------');
            setStatus('waiting');
            startPolling(storedId, storedToken);
            return;
          }
        } catch (err) {
          // Token invalid, clear and re-register
          localStorage.removeItem('displayToken');
          localStorage.removeItem('displayId');
        }
      }

      // Register as new pending display
      const result = await apiCall('displays.registerPending', {
        deviceInfo: fp,
        fingerprint: fp.fingerprintHash,
      });

      setDisplayId(result.displayId);
      setDisplayToken(result.token);
      setPinCode(result.pinCode);
      
      // Store credentials
      localStorage.setItem('displayToken', result.token);
      localStorage.setItem('displayId', result.displayId.toString());

      setStatus('waiting');
      startPolling(result.displayId, result.token);

    } catch (err) {
      console.error('Setup error:', err);
      setError(err.message || 'Failed to initialize display');
      setStatus('error');
    }
  };

  const startPolling = (id, token) => {
    // Poll every 3 seconds to check if approved
    pollRef.current = setInterval(async () => {
      try {
        const result = await apiCall('displays.checkStatus', {
          displayId: parseInt(id),
          token: token,
        });

        if (result.status === 'active' && result.isPinAccepted) {
          setStatus('approved');
          clearInterval(pollRef.current);
          
          // Redirect to player after showing success
          setTimeout(() => {
            window.location.href = '/player';
          }, 3000);
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    }, 3000);
  };

  // Render loading state
  if (status === 'initializing' || status === 'collecting') {
    return (
      <>
        <style>{styles}</style>
        <div className="loading-overlay">
          <div className="loading-spinner" />
          <div className="loading-text">
            {status === 'initializing' ? 'Initializing...' : 'Collecting device information...'}
          </div>
        </div>
      </>
    );
  }

  // Render error state
  if (status === 'error') {
    return (
      <>
        <style>{styles}</style>
        <div className="setup-container">
          <div className="background-grid" />
          <div className="pairing-card">
            <h1 style={{ color: '#ef4444', marginBottom: 16 }}>Setup Error</h1>
            <p style={{ color: '#94a3b8', marginBottom: 24 }}>{error}</p>
            <button 
              onClick={() => window.location.reload()}
              style={{
                padding: '12px 24px',
                background: '#3b82f6',
                border: 'none',
                borderRadius: 8,
                color: 'white',
                fontSize: 16,
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </>
    );
  }

  // Render approved/success state
  if (status === 'approved') {
    return (
      <>
        <style>{styles}</style>
        <div className="setup-container">
          <div className="background-grid" />
          <div className="pairing-card success-container">
            <CheckCircle />
            <h1 className="success-title">Display Paired!</h1>
            <p className="success-message">This display has been successfully activated.</p>
            <div className="redirect-notice">
              <Loader />
              <span>Starting player...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Render waiting for approval state
  return (
    <>
      <style>{styles}</style>
      <div className="setup-container">
        <div className="background-grid" />
        
        <div className="hero-section">
          <Monitor />
          <h1 className="hero-title">Digital Signs</h1>
          <p className="hero-subtitle">Display Setup</p>
        </div>

        <div className="pairing-card">
          <div className="step-label">Step 1</div>
          <p className="instruction-text">
            On your computer or phone, go to:
          </p>
          <div className="url-display">
            <span className="url-text">{pairingUrl}</span>
          </div>

          <div className="divider">
            <span>then enter this pin</span>
          </div>

          <div className="pin-section">
            <div className="pin-display">
              {pinCode.split('').map((digit, i) => (
                <div key={i} className="pin-digit">{digit}</div>
              ))}
            </div>
          </div>

          <div className="status-section">
            <Clock />
            <span className="status-text">Waiting for approval...</span>
          </div>

          {fingerprint && (
            <div className="device-info">
              <div className="device-info-title">Device Information</div>
              <div className="device-info-grid">
                <div className="info-item">
                  <div className="info-label">Browser</div>
                  <div className="info-value">{fingerprint.browserName} {fingerprint.browserVersion}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Operating System</div>
                  <div className="info-value">{fingerprint.osName}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Screen</div>
                  <div className="info-value">{fingerprint.screenResolution}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Timezone</div>
                  <div className="info-value">{fingerprint.timezone}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="footer">
          Display ID: {displayId || '...'} • Fingerprint: {fingerprint?.fingerprintHash?.slice(0, 12)}...
        </div>
      </div>
    </>
  );
}
